import {
  CompositionSummaryValue,
  CompositionSummaryValues,
  EventDetailsDto,
  EventDetailsLine
} from "../../../models/event/event-details";
import {EventCompositionStat, EventOrderStats, EventRevenueStat} from "../../../models/event/event-order-stats";
import {BookingType, BookingTypeLabels} from "../../../models/payment/booking-type";
import {OrderHelper} from "./order-helper";
import {
  CompositionGroupingMode,
  compositionGroupingParameter,
  compositionGroupKey
} from "../../../_shared/composition-grouping";
import {ModificationLineTransfer} from "../../../_models/_booking/composition-override-linker-transfer";

/**
 * One row of a composition summary card, ready for the template: the bucket's own key (a stable `track`
 * identity — the article *name* is NOT unique once the same article is split into swap variants), plus its
 * aggregated values.
 */
export interface CompositionValuePair extends CompositionSummaryValue {
  /** The dictionary key this bucket was stored under — `compositionId`, or the composition+swap variant key. */
  key: string;
}

/**
 * The event's revenue as the „Umsatz" tile renders it (#817): the server's own figures plus the total the
 * page derives from them.
 */
export interface EventRevenue extends EventRevenueStat {
  /** As {@link EventRevenueStat.other}, defaulted to 0 so the tile never has to ask whether it is there. */
  other: number;
  /**
   * The revenue: the line prices after the order-level Rabatt and Aufschlag, plus the other revenue rows
   * booked on whole orders ({@link other}).
   */
  total: number;
}

export class EventHelper {
  public static getTotalNumeric(values: { [key: number]: { count: number, amount: number } } | {
    [key: number]: { amount: number }
  }): number {
    return Object.keys(values).reduce((acc, value) => {
      acc += values[parseInt(value)].amount;
      return acc;
    }, 0);
  }

  public static getTotalString(values: { [key: string]: { name: string, count: number, amount: number } }): number {
    return Object.keys(values).reduce((acc, value) => {
      acc += values[value].amount;
      return acc;
    }, 0);
  }

  public static getCountNumeric(values: { [key: number]: { count: number, amount: number } }): number {
    return Object.keys(values).reduce((acc, value) => {
      acc += values[parseInt(value)].count;
      return acc;
    }, 0);
  }

  public static getCountString(values: { [key: string]: { name: string, count: number, amount: number } }): number {
    return Object.keys(values).reduce((acc, value) => {
      acc += values[value].count;
      return acc;
    }, 0);
  }

  /**
   * The event's total turnover — the „Gesamtumsatz"/„Gesamtbetrag" tiles and the two card labels that repeat
   * it.
   *
   * Reads the SERVER's event-wide aggregate when there is one (#716) and falls back to summing the client-side
   * `bookingValues` dictionary otherwise. The distinction matters the moment the order list is bounded: the
   * dictionary then describes the loaded PAGE, and a tile that quietly reported a page total while a venue was
   * selling is precisely the failure mode Bug #408 already produced once.
   */
  public static getEventTotal(event: EventDetailsDto): number {
    const stats = event.orderStats;
    if (stats !== undefined) return stats.bookingTypes.reduce((sum, bucket) => sum + bucket.amount, 0);
    return this.getTotalNumeric(event.bookingValues);
  }

  /**
   * The event's revenue (#817), or `undefined` when the payload carries none — a server that predates the
   * field, or a `?fields=` projection that leaves `orderStats` out. The page then keeps the „Gesamtbetrag"
   * tile it always had rather than inventing the figure from the loaded orders: the orders on the page are a
   * bounded slice, and a revenue folded from them would understate exactly while a venue is selling.
   *
   * `total` is derived here from the parts the tile shows, so the tile cannot print a total that disagrees
   * with its own rows. `other` — deposits refunded, single-purpose vouchers, advances — is read as 0 when the
   * server predates the #817 follow-up, which is exactly what that server's own total was.
   */
  public static getRevenue(event: EventDetailsDto): EventRevenue | undefined {
    const revenue = event.orderStats?.revenue;
    if (revenue === undefined) return undefined;
    const other = revenue.other ?? 0;
    return {...revenue, other, total: revenue.articles + revenue.discount + revenue.surcharge + other};
  }

  /** The „Artikel" tile's count — server-wide when available, see {@link getEventTotal}. */
  public static getArticleCount(event: EventDetailsDto): number {
    const stats = event.orderStats;
    if (stats !== undefined) return stats.compositions.reduce((sum, bucket) => sum + bucket.quantity, 0);
    return this.getCountString(event.normalCompositionValues);
  }

  /** The „Artikel" tile's amount — server-wide when available, see {@link getEventTotal}. */
  public static getArticleTotal(event: EventDetailsDto): number {
    const stats = event.orderStats;
    if (stats !== undefined) return stats.compositions.reduce((sum, bucket) => sum + bucket.amount, 0);
    return this.getTotalString(event.normalCompositionValues);
  }

  /** The „Sonstiges" tile's count — server-wide when available, see {@link getEventTotal}. */
  public static getOtherCount(event: EventDetailsDto): number {
    const stats = event.orderStats;
    if (stats !== undefined) return stats.others.reduce((sum, bucket) => sum + bucket.quantity, 0);
    return this.getCountString(event.otherCompositionValues);
  }

  /** The „Sonstiges" tile's amount — server-wide when available, see {@link getEventTotal}. */
  public static getOtherTotal(event: EventDetailsDto): number {
    const stats = event.orderStats;
    if (stats !== undefined) return stats.others.reduce((sum, bucket) => sum + bucket.amount, 0);
    return this.getTotalString(event.otherCompositionValues);
  }

  /**
   * Folds an event's per-transaction-type totals into `event.bookingValues`.
   *
   * **Typed structurally rather than on the payload** (Feature #760): the event-details page reads
   * {@link EventDetailsDto}, while the employee sales-by-event read still answers `EventEdto` through a
   * `?fields=` projection. Both satisfy this shape, and this fold does not care which — it reads the server
   * statistics when there are any and the loaded orders' own dictionaries otherwise.
   */
  public static setEventBookingValues(event: EventBookingValueSource) {
    // #716: the server already grouped every order of the event by transaction type, including the
    // order-level business cases, and already dropped the employee bucket. Re-folding the loaded page on top
    // of that would only be able to make it smaller.
    const stats = event.orderStats;
    if (stats !== undefined) {
      const fromServer: { [key: number]: { count: number, amount: number } } = {};
      stats.bookingTypes.forEach(bucket => fromServer[bucket.bookingType] = {
        count: bucket.count,
        amount: bucket.amount
      });
      event.bookingValues = fromServer;
      return;
    }

    let result: { [key: number]: { count: number, amount: number } } = {};
    event.orders.reduce((acc, value) => {
      Object.keys(value.bookingValues).forEach(key => {
        let k = parseInt(key);
        if (k == BookingType.Employee) return;

        if (acc[k] == undefined) acc[k] = {count: 0, amount: 0};
        acc[k].count += value.bookingValues[k].count;
        acc[k].amount += value.bookingValues[k].amount;
      })
      return acc;
    }, result);
    event.bookingValues = result;
  }

  /**
   * Aggregates the "Sonstiges" bucket (compositions carrying an `other` tag) into
   * {@link EventDetailsDto.otherCompositionValues}.
   *
   * @param event the extended event to aggregate; its `otherCompositionValues` is replaced
   * @param mode the Feature #534 grouping, used ONLY on the no-statistics fallback path: since #716 the
   *   server aggregates at the grain the request asked for, so a payload carrying `orderStats` is already
   *   bucketed and this argument does not re-bucket it. `'combined'` buckets by article, `'individual'` by
   *   article + swap combination; the totals are identical either way — only the number of rows, and their
   *   sub-line wording, differ.
   */
  public static setOtherCompositionValues(event: EventDetailsDto, mode: CompositionGroupingMode = 'combined') {
    event.otherCompositionValues =
      this.compositionValues(event, mode, stats => stats.others, composition => this.isOther(composition));
  }

  /**
   * The summary rows for one dictionary, sorted by descending count (unchanged behaviour).
   *
   * The `key` is carried through deliberately: in `'individual'` mode the same article appears once per swap
   * combination, so `name` is no longer unique and a template `track` on it would collapse rows onto one
   * another (Angular throws on duplicate track keys). Track on `key`.
   */
  public static getCompositionValuePairs(values: CompositionSummaryValues): CompositionValuePair[] {
    return Object.keys(values)
      .map(key => ({key, ...values[key]}))
      .sort((a, b) => b.count - a.count);
  }

  public static getBookingValuePairs(values: { [key: number]: { count: number, amount: number } }): {
    type: string,
    count: number,
    amount: number
  }[] {
    return Object.keys(values).map(key => {
      let k = parseInt(key);
      return {type: BookingTypeLabels[k], count: values[k].count, amount: values[k].amount};
    }).sort((a, b) => b.amount - a.amount);
  }

  /**
   * Aggregates the "Artikel" bucket (every composition NOT carrying an `other` tag) into
   * {@link EventDetailsDto.normalCompositionValues}. See {@link setOtherCompositionValues} for `mode`.
   */
  public static setNormalCompositionValues(event: EventDetailsDto, mode: CompositionGroupingMode = 'combined') {
    event.normalCompositionValues =
      this.compositionValues(event, mode, stats => stats.compositions, composition => !this.isOther(composition));
  }

  /**
   * The rows of one composition card — from the SERVER's event-wide aggregate, in BOTH grouping modes (#716).
   *
   * **Both modes, and that is the point.** The server used to aggregate only at composition grain, i.e. only
   * the `'combined'` bucketing, so the `'individual'` split (#534) was folded here from whatever orders the
   * page had loaded. With the default page of 100 that described the newest 100 transactions of an evening —
   * while a venue was still selling, and in the mode most users actually work in, because the difference
   * between a Vodka Cola and a Vodka Orange is what they are reading the card for. The split is now a SQL
   * aggregate over the whole window too, keyed by the composite id computed in the database and carrying the
   * same `InterchangedComponentTextFactory` wording the order table renders.
   *
   * **The fold below survives for the payloads whose grain is not the one being asked for**, and there are
   * exactly two of those:
   *
   * - a payload with NO statistics at all — a pre-#716 server, or a `?fields=` projection that omits
   *   `OrderStats`. Unchanged behaviour: fold the loaded orders.
   * - the moment after the user flips „Kombinationen einzeln". The grain is a SERVER grain now, so the page
   *   refetches; until that answer lands, the snapshot in memory carries the OTHER grain and folding it is
   *   what keeps the click instant. That fold is page-scoped and therefore transient — it is replaced by the
   *   server's event-wide rows on the next render, and the TILES never come from it at all (they read
   *   {@link getArticleTotal} and friends, which always read the server aggregate).
   *
   * A payload that does not say which grain it carries is treated as `'Combined'`, which is what every
   * pre-#722 server produced.
   */
  private static compositionValues(event: EventDetailsDto, mode: CompositionGroupingMode,
                                   select: (stats: EventOrderStats) => EventCompositionStat[],
                                   include: (composition: EventDetailsLine) => boolean)
    : CompositionSummaryValues {
    const stats = event.orderStats;
    if (stats === undefined) return this.aggregateCompositionValues(event, mode, include);
    if ((stats.grouping ?? 'Combined') !== compositionGroupingParameter(mode)) {
      return this.aggregateCompositionValues(event, mode, include);
    }

    const result: CompositionSummaryValues = {};
    select(stats).forEach(bucket => result[bucket.key ?? bucket.id] = {
      name: bucket.name,
      count: bucket.quantity,
      amount: bucket.amount,
      // Empty in `'combined'` mode by design — a merged bucket spans several swap combinations and has no
      // single wording — which is what the server sends, so no branch is needed here. `?? []` is the #440
      // guard: the field is optional on the wire and genuinely ABSENT from a projection that does not name it.
      modificationLines: bucket.modificationLines ?? []
    });
    return result;
  }

  public static setLocationValues(event: EventDetailsDto) {
    // #716 — see setEventBookingValues.
    const stats = event.orderStats;
    if (stats !== undefined) {
      const fromServer: { [key: string]: { name: string, count: number, amount: number } } = {};
      stats.locations.forEach(bucket => fromServer[bucket.id] = {
        name: bucket.name,
        count: bucket.count,
        amount: bucket.amount
      });
      event.locationValues = fromServer;
      return;
    }

    let result: { [key: string]: { name: string, count: number, amount: number } } = {};
    event.orders
      .filter(order => order.bookingType != BookingType.Employee)
      .reduce((acc, value) => {
        if (acc[value.location.id] == undefined) acc[value.location.id] = {
          name: value.location.name,
          count: 0,
          amount: 0
        };
        acc[value.location.id].count += 1;
        acc[value.location.id].amount += OrderHelper.getOrderTotal(value);
        return acc;
      }, result);
    event.locationValues = result;
  }

  /**
   * The one aggregation both composition summaries share: walk every non-employee order's composition lines,
   * keep the ones the caller's `include` predicate accepts, and sum them into a bucket per
   * {@link compositionGroupKey}.
   *
   * Two Feature #534 details worth keeping straight:
   *
   * - The bucket key is the ONLY thing the mode changes. Counts and amounts are summed identically, so
   *   `getCountString`/`getTotalString` over the result — and therefore the "Artikel: N" card label and the
   *   hero tiles — read the same number in both modes. A test pins that.
   * - `modificationLines` is populated only in `'individual'` mode, from the FIRST line seen in a bucket.
   *   Every line sharing a variant key has, by construction of the composite id, the same interchanged
   *   components and therefore the same #197/#198 wording — so "first seen" is not a heuristic, it is the
   *   bucket's wording. In `'combined'` mode the bucket spans several combinations at once and the array is
   *   deliberately left empty rather than showing one arbitrary variant's lines as if it described all of them.
   *
   * `?? []` on the wire field is the #440 guard: `modificationLines` is optional on
   * `CompositionOverrideLinkerTransfer` and is genuinely ABSENT when a `?fields=` projection does not name it.
   */
  private static aggregateCompositionValues(event: EventDetailsDto, mode: CompositionGroupingMode,
                                            include: (composition: EventDetailsLine) => boolean)
    : CompositionSummaryValues {
    const result: CompositionSummaryValues = {};

    (event.orders ?? [])
      .filter(order => order.bookingType != BookingType.Employee)
      .forEach(order => {
        (order.compositions ?? []).forEach(composition => {
          if (!include(composition)) return;

          const key = compositionGroupKey(
            {compositeId: composition.compositeId, compositionId: composition.composition.id}, mode);

          this.initializeCompositionValues(result, key, composition.composition.name,
            mode === 'individual' ? composition.modificationLines ?? [] : []);
          result[key].count += composition.quantity;
          result[key].amount += composition.quantity * composition.price;
        });
      });

    return result;
  }

  /**
   * Does this line belong to "Sonstiges" rather than "Artikel"?
   *
   * One field read, since Feature #760. It used to be `(composition.composition.tags ?? []).findIndex(x =>
   * x.other) != -1`, and that `?? []` was load-bearing rather than decorative: `Tags{Other}` was a PROJECTED
   * field, so a `?fields=` string that did not name it shipped the composition with no `tags` property at all
   * and the unguarded dereference threw mid-render (#440) — on every change-detection pass, because this runs
   * again on every grouping-mode flip and every live refresh. A boolean the server decided cannot be absent
   * and cannot be read two ways.
   */
  private static isOther(composition: EventDetailsLine): boolean {
    return composition.composition.other;
  }

  private static initializeCompositionValues(collection: CompositionSummaryValues, key: string, name: string,
                                             modificationLines: ModificationLineTransfer[]) {
    if (collection[key] == undefined) collection[key] = {
      name: name,
      count: 0,
      amount: 0,
      modificationLines: modificationLines
    };
  }
}

/**
 * The minimum an event has to be for {@link EventHelper.setEventBookingValues} to fold it — see that method
 * for why it is a structural type rather than one of the two payloads.
 */
export interface EventBookingValueSource {
  orderStats?: EventOrderStats;
  orders: { bookingValues: { [key: number]: { count: number, amount: number } } }[];
  bookingValues: { [key: number]: { count: number, amount: number } };
}
